/**
 * Customer.io Web Tracking Service  
 * Handles email collection, user identification, and event tracking
 * 
 * NOTE: Uses backend API to avoid CORS issues with Customer.io's CDP API.
 * Customer.io's CDP API is server-side only for security reasons.
 */

interface CustomerIOConfig {
  cdpApiKey: string;
  siteId: string;
  region?: 'us' | 'eu';
  debug?: boolean;
}

interface IdentifyParams {
  userId: string;
  traits?: Record<string, any>;
}

interface TrackEventParams {
  userId?: string;
  anonymousId?: string;
  event: string;
  properties?: Record<string, any>;
}

export class CustomerIOService {
  private config: CustomerIOConfig;
  private baseUrl: string;

  constructor(config: CustomerIOConfig) {
    this.config = config;
    // Use our backend API to avoid CORS issues with Customer.io
    this.baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';
    
    if (config.debug) {
      console.log('🎯 Customer.io Service initialized (via backend API):', { 
        siteId: config.siteId, 
        region: config.region || 'us',
        backendUrl: this.baseUrl 
      });
    }
  }

  /**
   * Identify a user in Customer.io (via backend API)
   */
  async identify(_params: IdentifyParams): Promise<boolean> {
    // This method is not used directly anymore - we use collectEmail instead
    if (this.config.debug) {
      console.log('🎯 Customer.io identify called (will be handled by collectEmail)');
    }
    return true;
  }

  /**
   * Track an event in Customer.io (via backend API)
   */
  async track(_params: TrackEventParams): Promise<boolean> {
    // This method is not used directly anymore - we use collectEmail instead
    if (this.config.debug) {
      console.log('🎯 Customer.io track called (will be handled by collectEmail)');
    }
    return true;
  }

  /**
   * Handle email collection forms - uses backend API to avoid CORS
   */
  async collectEmail(email: string, options: {
    source: string;
    feature?: string;
    userId?: string;
    additionalTraits?: Record<string, any>;
  }): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/email-collection/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Include auth cookies if available
        body: JSON.stringify({
          email,
          source: options.source,
          feature: options.feature,
          additional_traits: options.additionalTraits,
        }),
      });

      const result = await response.json();
      const success = response.ok && result.success;
      
      if (this.config.debug) {
        console.log(`🎯 Customer.io collectEmail ${success ? 'success' : 'failed'}:`, {
          email,
          source: options.source,
          feature: options.feature,
          response: result,
          status: response.status
        });
      }

      return success;
    } catch (error) {
      console.error('🚨 Customer.io collectEmail error:', error);
      return false;
    }
  }
}

// Create singleton instance
const customerIOConfig: CustomerIOConfig = {
  cdpApiKey: import.meta.env.VITE_CUSTOMERIO_CDP_API_KEY || '',
  siteId: import.meta.env.VITE_CUSTOMERIO_SITE_ID || '',
  region: 'us', // Adjust if you're using EU region
  debug: import.meta.env.VITE_CUSTOMERIO_DEBUG === 'true' || import.meta.env.DEV,
};

// Export singleton instance
export const customerIO = new CustomerIOService(customerIOConfig);

// Export types for TypeScript
export type { CustomerIOConfig, IdentifyParams, TrackEventParams };
